import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import type { Design, DesignInsert, DesignUpdate } from '@/lib/database.types'

export function useDesigns(projectId?: string) {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const {
    data: designs = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['designs', projectId || 'all', user?.id],
    queryFn: async (): Promise<Design[]> => {
      if (!user?.id) return []
      
      let query = supabase
        .from('designs')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
      
      if (projectId) {
        query = query.eq('project_id', projectId)
      }
      
      const { data, error } = await query.limit(50)
      
      if (error) throw error
      return data || []
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2,
  })

  const createDesignMutation = useMutation({
    mutationFn: async (design: Omit<DesignInsert, 'user_id'>) => {
      if (!user?.id) throw new Error('Not authenticated')
      
      const { data, error } = await supabase
        .from('designs')
        .insert({ ...design, user_id: user.id })
        .select()
        .single()
      
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designs'] })
    },
  })

  const updateDesignMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: DesignUpdate }) => {
      const { data, error } = await supabase
        .from('designs')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designs'] })
    },
  })

  const deleteDesignMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('designs')
        .delete()
        .eq('id', id)
      
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designs'] })
    },
  })

  return {
    designs,
    isLoading,
    error,
    refetch,
    createDesign: createDesignMutation.mutateAsync,
    updateDesign: updateDesignMutation.mutateAsync,
    deleteDesign: deleteDesignMutation.mutateAsync,
    isCreating: createDesignMutation.isPending,
    isUpdating: updateDesignMutation.isPending,
    isDeleting: deleteDesignMutation.isPending,
  }
}

export function useDesign(designId: string | undefined) {
  const { user } = useAuthStore()

  return useQuery({
    queryKey: ['design', designId],
    queryFn: async (): Promise<Design | null> => {
      if (!designId) return null
      
      const { data, error } = await supabase
        .from('designs')
        .select('*')
        .eq('id', designId)
        .single()
      
      if (error) {
        if (error.code === 'PGRST116') return null
        throw error
      }
      return data
    },
    enabled: !!designId && !!user?.id,
  })
}
